<template>
  <div
    class="
      tw-group
      tw-relative
      tw-rounded-lg
      tw-border
      tw-border-solid
      tw-overflow-hidden
      tw-transition-shadow
    "
    :class="containerBgClass"
    :data-testid="`sf-metric-pill-${variant}`"
  >
    <!-- Collapsed states (variant-specific layout + shared hover affordance) -->
    <template v-if="!expanded">
      <!-- Default variant -->
      <button
        v-if="variant === 'default'"
        type="button"
        class="
          tw-w-full
          tw-h-full
          tw-flex
          tw-items-stretch
          tw-gap-3
          tw-px-4
          tw-py-3
          tw-bg-transparent
          tw-border-0
          tw-cursor-pointer
          tw-text-left
          tw-font-sans
        "
        data-testid="sf-metric-pill-row"
        @click="handleExpand"
      >
        <span
          class="
            tw-flex-1
            tw-flex
            tw-items-center
            tw-min-w-0
            tw-text-xs
            tw-text-sf-neutral-dark-1
          "
        >
          <span class="tw-line-clamp-2">
            {{ graph?.title ?? '' }}
          </span>
        </span>
        <span class="tw-flex tw-flex-col tw-items-end tw-gap-0.5">
          <span class="tw-font-bold tw-text-sm tw-text-sf-neutral-dark-1">
            {{ value }}
          </span>
          <span
            class="tw-text-xs"
            :class="delta ? deltaColorClass : 'tw-invisible'"
          >
            {{ delta?.text ?? '—' }}
          </span>
        </span>
      </button>

      <!-- Slim variant: full card is the click target.
           `.stop` prevents activating the wrapping link when the pill is
           rendered inside pinned-report-card. -->
      <button
        v-else-if="variant === 'slim'"
        type="button"
        class="
          tw-w-full
          tw-h-full
          tw-flex
          tw-flex-col
          tw-items-start
          tw-gap-1
          tw-px-4
          tw-py-3
          tw-bg-transparent
          tw-border-0
          tw-cursor-pointer
          tw-text-left
          tw-font-sans
        "
        data-testid="sf-metric-pill-card"
        @click.stop="handleExpand"
      >
        <span
          class="
            tw-truncate tw-max-w-[14ch] tw-text-xs tw-text-sf-neutral-light-2
          "
        >
          {{ graph?.title ?? '' }}
        </span>
        <span class="tw-font-bold tw-text-md tw-text-sf-neutral-dark-1">
          {{ value }}
        </span>
        <span
          class="tw-text-xs"
          :class="delta ? deltaColorClass : 'tw-invisible'"
        >
          {{ delta?.text ?? '—' }}
        </span>
      </button>
    </template>

    <!-- Expanded (shared across variants) -->
    <div
      v-else
      class="tw-divide-y tw-divide-solid tw-divide-sf-snow"
      data-testid="sf-metric-pill-expanded"
    >
      <div class="tw-flex tw-items-start tw-gap-3 tw-px-4 tw-py-3">
        <SfButtonDs
          v-if="!hideCollapse"
          icon-only
          icon-before="minus"
          variant="secondary"
          size="sm"
          :aria-label="collapseLabel"
          data-testid="sf-metric-pill-collapse"
          @click="handleCollapse"
        />
        <span
          class="tw-flex-1 tw-font-medium tw-text-sm tw-text-sf-neutral-dark-1"
        >
          {{ graph?.title ?? '' }}
        </span>
        <span class="tw-flex tw-flex-col tw-items-end tw-gap-0.5">
          <span class="tw-font-bold tw-text-base tw-text-sf-neutral-dark-1">
            {{ value }}
          </span>
          <span v-if="delta" class="tw-text-xs" :class="deltaColorClass">
            {{ delta.text }}
          </span>
        </span>
      </div>

      <div class="tw-p-4 tw-border-0" :style="{ height: chartHeight }">
        <ChartLoadState
          :graph-data="graphData"
          :loading="loading"
          :errored="errored"
          :error-text="loadErrorText"
          :hide-x-axis-labels="hideXAxisLabels"
          :compact-y-axis="compactYAxis"
        />
      </div>

      <div
        v-if="
          !readOnly && (showMainChartToggle || showHighlightButton || showEdit)
        "
        class="
          tw-flex
          tw-flex-wrap
          tw-items-center
          tw-justify-between
          tw-gap-2
          tw-p-3
          tw-border-0
        "
        data-testid="sf-metric-pill-actions"
      >
        <SfMetricControls
          v-if="showMainChartToggle || showHighlightButton"
          :show-main-chart="showMainChartToggle"
          :show-pin="showHighlightButton"
          :main-chart="pinned ?? false"
          :pinned="highlighted ?? false"
          :main-chart-disabled="pinDisabled"
          :main-chart-disabled-tooltip="pinDisabledTooltip"
          :pinned-disabled="highlightDisabled"
          :pinned-disabled-tooltip="highlightDisabledTooltip"
          :main-chart-label="mainChartLabel"
          :pin-label="highlightLabel"
          :unpin-label="unhighlightLabel"
          @update:main-chart="onMainChartToggle"
          @update:pinned="onHighlightChange"
        />
        <span
          v-if="showEdit"
          v-tooltip="editDisabledTooltipBinding"
          class="tw-flex"
        >
          <SfButtonDs
            size="md"
            variant="secondary"
            icon-before="pen"
            :disabled="editDisabled"
            data-testid="sf-metric-pill-edit"
            @click="handleEdit"
          >
            {{ editLabel }}
          </SfButtonDs>
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import ChartLoadState from '@app/application/report/components/chart-load-state.vue'
import type {
  GraphData,
  ReportGraph,
} from '@app/application/report/screens/graphs-echarts/types'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfMetricControls from '@app/shared/vue/components/sf-metric-controls/sf-metric-controls.vue'
import type {
  MetricPillDelta,
  MetricPillVariant,
} from '@app/shared/vue/components/sf-metric-pill/types'

defineOptions({
  name: 'SfMetricPill',
})

interface Props {
  graph: ReportGraph | null
  graphData?: GraphData | null
  value: string
  delta?: MetricPillDelta
  variant: MetricPillVariant
  readOnly?: boolean
  pinned?: boolean
  highlighted?: boolean
  highlightDisabled?: boolean
  highlightDisabledTooltip?: string
  pinDisabled?: boolean
  pinDisabledTooltip?: string
  hideCollapse?: boolean
  hideXAxisLabels?: boolean
  compactYAxis?: boolean
  loading?: boolean
  errored?: boolean
  loadErrorText?: string
  showEdit?: boolean
  editDisabled?: boolean
  editDisabledTooltip?: string
  mainChartLabel?: string
  highlightLabel?: string
  unhighlightLabel?: string
  editLabel?: string
  collapseLabel?: string
  chartHeight?: string
}

const props = withDefaults(defineProps<Props>(), {
  graphData: null,
  delta: undefined,
  readOnly: false,
  pinned: undefined,
  highlighted: undefined,
  highlightDisabled: false,
  highlightDisabledTooltip: undefined,
  pinDisabled: false,
  pinDisabledTooltip: undefined,
  hideCollapse: false,
  hideXAxisLabels: false,
  compactYAxis: false,
  loading: false,
  errored: false,
  loadErrorText: '',
  showEdit: false,
  editDisabled: false,
  editDisabledTooltip: undefined,
  mainChartLabel: undefined,
  highlightLabel: undefined,
  unhighlightLabel: undefined,
  editLabel: undefined,
  collapseLabel: undefined,
  chartHeight: '260px',
})

const expanded = defineModel<boolean>('expanded', { required: true })

const emit =
  defineEmits<{
    expand: []
    edit: []
    'update:pinned': [value: boolean]
    'update:highlighted': [value: boolean]
  }>()

const editDisabledTooltipBinding = computed(() =>
  props.editDisabled ? props.editDisabledTooltip ?? '' : ''
)

const showMainChartToggle = computed(() => props.pinned !== undefined)
const showHighlightButton = computed(() => props.highlighted !== undefined)

const containerBgClass = computed(() =>
  expanded.value
    ? 'tw-bg-white tw-border-sf-neutral-subtle-2'
    : 'tw-bg-sf-slate-subtle-2 tw-border-transparent hover:tw-bg-sf-slate-lighter-1 hover:tw-shadow-md hover:tw-border-sf-primary'
)

const deltaColorClass = computed(() => {
  switch (props.delta?.direction) {
    case 'up':
      return 'tw-text-sf-success-light-2'
    case 'down':
      return 'tw-text-sf-danger-light-2'
    default:
      return 'tw-text-sf-neutral-light-2'
  }
})

function handleExpand(): void {
  expanded.value = true
  emit('expand')
}

function handleCollapse(): void {
  expanded.value = false
}

function onMainChartToggle(next: boolean): void {
  if (props.pinDisabled) return
  emit('update:pinned', next)
}

function onHighlightChange(next: boolean): void {
  if (props.highlightDisabled) return
  emit('update:highlighted', next)
}

function handleEdit(): void {
  if (props.editDisabled) return
  emit('edit')
}
</script>
